import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  formatDateMMMddYYYY,
  getErrorResponse,
  toastError,
} from "../../utils/util";
import RdMyModal from "../../widgets/myModal/RdMyModal";
import AddFormItem from "./AddFormItem";
import AddIcon from "@mui/icons-material/Add";
import AirplanemodeInactiveIcon from "@mui/icons-material/AirplanemodeInactive";
import UpdateIcon from "@mui/icons-material/Update";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import SearchIcon from "@mui/icons-material/Search";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";

import { API_ENDPOINT } from "../../utils/constants";
import SimpleYesNoDialog from "../../widgets/SimpleYesNoDialog";
import CustomCircularP from "../../widgets/CustomCircularP";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useApiEp from "../../hooks/useApiEp";
import { getHeader } from "../../utils/utils2";

const FormItems = () => {
  const navigate = useNavigate();
  const [reloadItems, setReloadItems] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState("");

  const {
    data: formItems,
    isLoading,
    isError,
    error,
  } = useApiEp(`/admin/get-form-items?reload=${reloadItems}`);
  const [showAddItem, setShowAddItem] = useState(false);
  const [actionMode, setActionMode] = useState("add");
  const [showDeleteItem, setShowDeleteItem] = useState(false);
  const [deletingItem, setDeletingItem] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);

  const open = Boolean(anchorEl);

  if (isLoading) {
    return <LinearProgress sx={{ my: 2 }} />;
  }
  if (isError) {
    return <Alert severity="error" sx={{ my: 2 }}>{getErrorResponse(error)}</Alert>;
  }

  const handleMenuClick = (event, item) => {
    setAnchorEl(event.currentTarget);
    setSelectedItem(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleUpdateForm = (item) => {
    handleMenuClose();
    navigate(`/oes-form-builder/${item._id}`);
  };

  const handleEditItem = (item) => {
    setActionMode("edit");
    setCurrentItem(item);
    handleMenuClose();
    setShowAddItem((prev) => !prev);
  };

  const handleDeleteItem = (item) => {
    setCurrentItem(item);
    handleMenuClose();
    setShowDeleteItem((prev) => !prev);
  };

  const handleAddItem = () => {
    setActionMode("add");
    setShowAddItem((prev) => !prev);
  };

  const onAddedItem = () => {
    setShowAddItem((prev) => !prev);
    setReloadItems((prev) => !prev);
  };

  const onYesDelete = async () => {
    setShowDeleteItem((prev) => !prev);
    if (!currentItem) return;
    try {
      setDeletingItem(true);
      const { data } = await axios.delete(
        `${API_ENDPOINT}/admin/delete-form-item?id=${currentItem._id}`,
        getHeader()
      );
      toast.success(data.message);
      setReloadItems((prev) => !prev);
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingItem(false);
    }
  };

  const handleDisableForm = async (item) => {
    try {
      setDeletingItem(true);
      const { data } = await axios.post(
        `${API_ENDPOINT}/admin/disable-form-item?id=${item._id}`,
        { status: item.disabled !== true },
        getHeader()
      );
      toast.success(data.message);
      setReloadItems((prev) => !prev);
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingItem(false);
    }
  };

  const filteredItems = (formItems || []).filter((item) => {
    const q = searchQuery.toLowerCase();
    return (
      item.name?.toLowerCase().includes(q) ||
      String(item.serial).toLowerCase().includes(q)
    );
  });

  return (
    <Box>
      <CustomCircularP show={deletingItem} />
      <SimpleYesNoDialog
        title="Please confirm!"
        message="Are you sure you want to delete this form item?"
        show={showDeleteItem}
        setShow={setShowDeleteItem}
        onYes={onYesDelete}
      />
      <RdMyModal
        mWidth={"720px"}
        showModal={showAddItem}
        setShowModal={setShowAddItem}
        modalC={
          <AddFormItem
            show={showAddItem}
            onAdded={onAddedItem}
            mode={actionMode}
            editItem={currentItem}
          />
        }
      />

      {/* Toolbar: Search, View Switcher, Add Button */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <TextField
          size="small"
          placeholder="Search forms by name or serial..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 260,
            flex: { sm: "0 1 320px" },
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              bgcolor: "#FFFFFF",
            },
          }}
        />

        <Stack direction="row" spacing={1.5} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, val) => val && setViewMode(val)}
            size="small"
            sx={{ bgcolor: "#FFFFFF", borderRadius: "10px" }}
          >
            <ToggleButton value="grid" aria-label="grid view">
              <GridViewIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value="list" aria-label="list view">
              <ViewListIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            onClick={handleAddItem}
            size="medium"
            startIcon={<AddIcon />}
            variant="contained"
            disableElevation
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              bgcolor: "#7C3AED",
              "&:hover": { bgcolor: "#6D28D9" },
            }}
          >
            Add Form Item
          </Button>
        </Stack>
      </Stack>

      {filteredItems.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 5,
            textAlign: "center",
            borderRadius: "14px",
            bgcolor: "#F8FAFC",
            border: "1px dashed #CBD5E1",
          }}
        >
          <Typography variant="h6" color="text.secondary" fontWeight={600}>
            {searchQuery ? "No matching form items found" : "No form names added yet"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {searchQuery ? "Try searching with a different term" : "Click 'Add Form Item' above to create your first form."}
          </Typography>
        </Paper>
      )}

      {/* Grid View */}
      {viewMode === "grid" && filteredItems.length > 0 && (
        <Grid container spacing={2.5}>
          {filteredItems.map((item, index) => {
            const isDisabled = Boolean(item.disabled);
            return (
              <Grid item xs={12} sm={6} md={4} key={item._id || index}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    borderRadius: "14px",
                    border: "1px solid #E2E8F0",
                    bgcolor: isDisabled ? "#F8FAFC" : "#FFFFFF",
                    opacity: isDisabled ? 0.75 : 1,
                    position: "relative",
                    overflow: "hidden",
                    boxShadow: "0px 2px 8px rgba(15, 23, 42, 0.04)",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0px 10px 25px rgba(15, 23, 42, 0.08)",
                      borderColor: isDisabled ? "#CBD5E1" : "#C4B5FD",
                    },
                  }}
                >
                  {/* Top Status Accent Bar */}
                  <Box
                    sx={{
                      height: 4,
                      width: "100%",
                      bgcolor: isDisabled ? "#94A3B8" : "#7C3AED",
                    }}
                  />

                  <CardContent sx={{ p: 2.5, pb: 1.5, flexGrow: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                      <Avatar
                        sx={{
                          bgcolor: isDisabled ? "#CBD5E1" : "#F5F3FF",
                          color: isDisabled ? "#475467" : "#7C3AED",
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          width: 38,
                          height: 38,
                          border: "1px solid",
                          borderColor: isDisabled ? "#E2E8F0" : "#DDD6FE",
                        }}
                      >
                        {item.serial || index + 1}
                      </Avatar>

                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, item)}
                        sx={{ color: "#64748B" }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Stack>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.05rem",
                        color: "#0F172A",
                        mb: 1,
                        lineHeight: 1.3,
                      }}
                    >
                      {item.name}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" gap={0.75} mt={1.5}>
                      {item.expiry_date && (
                        <Chip
                          icon={<CalendarTodayIcon style={{ fontSize: 13 }} />}
                          label={formatDateMMMddYYYY(item.expiry_date)}
                          size="small"
                          sx={{
                            bgcolor: "#F1F5F9",
                            color: "#475467",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                          }}
                        />
                      )}

                      <Chip
                        icon={
                          isDisabled ? (
                            <BlockIcon style={{ fontSize: 13 }} />
                          ) : (
                            <CheckCircleOutlineIcon style={{ fontSize: 13 }} />
                          )
                        }
                        label={isDisabled ? "Disabled" : "Active"}
                        size="small"
                        color={isDisabled ? "default" : "success"}
                        variant={isDisabled ? "outlined" : "soft"}
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          bgcolor: isDisabled ? "#F1F5F9" : "#DCFCE7",
                          color: isDisabled ? "#64748B" : "#15803D",
                        }}
                      />
                    </Stack>
                  </CardContent>

                  {/* Bottom Action Footer */}
                  <Box sx={{ p: 2, pt: 0 }}>
                    <Button
                      onClick={() => handleUpdateForm(item)}
                      fullWidth
                      size="small"
                      variant="outlined"
                      endIcon={<ArrowForwardIcon fontSize="small" />}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "#E2E8F0",
                        color: "#7C3AED",
                        bgcolor: "#F5F3FF",
                        py: 0.8,
                        "&:hover": {
                          borderColor: "#C4B5FD",
                          bgcolor: "#EDE9FE",
                        },
                      }}
                    >
                      Configure Form Fields
                    </Button>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* List View */}
      {viewMode === "list" && filteredItems.length > 0 && (
        <Stack spacing={1.5}>
          {filteredItems.map((item, index) => {
            const isDisabled = Boolean(item.disabled);
            return (
              <Card
                key={item._id || index}
                elevation={0}
                sx={{
                  bgcolor: isDisabled ? "#F8FAFC" : "#FFFFFF",
                  borderRadius: "12px",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 2,
                  transition: "box-shadow 0.2s",
                  "&:hover": { boxShadow: "0px 4px 12px rgba(15, 23, 42, 0.05)" },
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      bgcolor: isDisabled ? "#E2E8F0" : "#F5F3FF",
                      color: isDisabled ? "#64748B" : "#7C3AED",
                      fontWeight: 700,
                      width: 40,
                      height: 40,
                    }}
                  >
                    {item.serial || index + 1}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0F172A" }}>
                      {item.name}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" mt={0.25}>
                      <Typography variant="caption" color="textSecondary">
                        Expiry: {formatDateMMMddYYYY(item.expiry_date)}
                      </Typography>
                      <Chip
                        label={isDisabled ? "Disabled" : "Active"}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.68rem",
                          fontWeight: 600,
                          bgcolor: isDisabled ? "#F1F5F9" : "#DCFCE7",
                          color: isDisabled ? "#64748B" : "#15803D",
                        }}
                      />
                    </Stack>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => handleUpdateForm(item)}
                    sx={{ textTransform: "none", fontWeight: 600, color: "#7C3AED" }}
                  >
                    Configure
                  </Button>
                  <IconButton onClick={(e) => handleMenuClick(e, item)}>
                    <MoreVertIcon />
                  </IconButton>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Shared Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 2,
          sx: { borderRadius: "10px", minWidth: 160, border: "1px solid #E2E8F0" },
        }}
      >
        <MenuItem onClick={() => selectedItem && handleUpdateForm(selectedItem)}>
          <ListItemIcon>
            <UpdateIcon fontSize="small" sx={{ color: "#7C3AED" }} />
          </ListItemIcon>
          Configure Form
        </MenuItem>
        <MenuItem onClick={() => selectedItem && handleEditItem(selectedItem)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit Details
        </MenuItem>
        <MenuItem onClick={() => selectedItem && handleDisableForm(selectedItem)}>
          <ListItemIcon>
            <AirplanemodeInactiveIcon
              color={selectedItem?.disabled ? "success" : "error"}
              fontSize="small"
            />
          </ListItemIcon>
          {selectedItem?.disabled ? "Enable Form" : "Disable Form"}
        </MenuItem>
        <MenuItem onClick={() => selectedItem && handleDeleteItem(selectedItem)} sx={{ color: "#DC2626" }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" sx={{ color: "#DC2626" }} />
          </ListItemIcon>
          Delete Form
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FormItems;
